import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Switch, message, Button, Space } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { api } from 'tools/rest';

/** Ключи в БД генерируются сами (f1, f2, …), пользователь вводит только подписи по-русски. */
function labelsToTempStorage(rows) {
  const out = {};
  let i = 1;
  rows.forEach((row) => {
    const label = (row && row.label && String(row.label).trim()) || '';
    if (label) {
      out[`f${i}`] = label;
      i += 1;
    }
  });
  return out;
}

function tempStorageToRows(ts) {
  if (!ts || typeof ts !== 'object' || Object.keys(ts).length === 0) {
    return [{ label: 'Стеллаж' }, { label: 'Полка' }];
  }
  return Object.entries(ts).map(([, label]) => ({ label: String(label || '') }));
}

const WarehouseFormModal = ({ visible, onClose, onSuccess, warehouse }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const isEdit = !!warehouse?.id;

  useEffect(() => {
    if (!visible) return;
    if (warehouse?.id) {
      form.setFieldsValue({
        name: warehouse.name,
        auto_approve: !!warehouse.auto_approve,
        placement_labels: tempStorageToRows(warehouse.temp_storage),
      });
    } else {
      form.setFieldsValue({
        name: '',
        auto_approve: false,
        placement_labels: [{ label: 'Стеллаж' }, { label: 'Полка' }],
      });
    }
  }, [visible, warehouse, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      const rows = values.placement_labels || [];
      const tempStorage = labelsToTempStorage(rows);
      if (Object.keys(tempStorage).length === 0) {
        message.error('Добавьте хотя бы одно поле размещения (подпись)');
        return;
      }
      setLoading(true);
      const body = {
        warehouse: {
          name: values.name.trim(),
          auto_approve: !!values.auto_approve,
          temp_storage: tempStorage,
        },
      };
      const req = isEdit
        ? api.put(`/api/v1/warehouses/${warehouse.id}.json`, body)
        : api.post('/api/v1/warehouses.json', body);

      req
        .then(() => {
          message.success(isEdit ? 'Склад сохранён' : 'Склад создан');
          form.resetFields();
          if (onSuccess) onSuccess();
          onClose();
        })
        .catch((err) => {
          message.error(
            err.response?.data?.errors?.join?.(', ') || 'Ошибка сохранения'
          );
        })
        .finally(() => setLoading(false));
    });
  };

  return (
    <Modal
      title={isEdit ? 'Редактирование склада' : 'Новый склад'}
      open={visible}
      onCancel={onClose}
      onOk={handleOk}
      confirmLoading={loading}
      okText={isEdit ? 'Сохранить' : 'Создать'}
      cancelText="Отмена"
      destroyOnHidden
      width={560}
    >
      <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
        <Form.Item
          name="name"
          label="Название"
          rules={[{ required: true, message: 'Введите название' }]}
        >
          <Input placeholder="Например, Основной склад" />
        </Form.Item>
        <Form.Item name="auto_approve" label="Автоподтверждение перемещений" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item label="Поля размещения на складе" required>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            Укажите только названия (как подписи в форме): стеллаж, полка, ряд и т.д. Порядок строк сохраняется.
          </div>
          <Form.List name="placement_labels">
            {(fields, { add, remove }) => (
              <>
                {fields.map((field) => (
                  <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                    <Form.Item
                      {...field}
                      name={[field.name, 'label']}
                      rules={[{ required: true, message: 'Введите подпись' }]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <Input placeholder="Например: Стеллаж" />
                    </Form.Item>
                    {fields.length > 1 ? (
                      <MinusCircleOutlined onClick={() => remove(field.name)} />
                    ) : null}
                  </Space>
                ))}
                <Button type="dashed" onClick={() => add({ label: '' })} block icon={<PlusOutlined />}>
                  Добавить поле
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default WarehouseFormModal;
